import { useState } from 'react';
import { PlatformApiError, type Idea, type SaveIdea } from '@sfc/sdk';
import { platform } from '../api/client';
import { SkillPicker } from '../skills/SkillPicker';
import { useSkills } from '../skills/useSkills';

const STAGES = [
  ['idea', 'Just an idea'],
  ['validating', 'Talking to people'],
  ['prototype', 'Prototype'],
  ['launched', 'Launched'],
] as const;

const COMPENSATION = [
  ['equity', 'Equity'],
  ['paid', 'Paid'],
  ['hybrid', 'Equity and pay'],
  ['portfolio', 'For the portfolio'],
] as const;

const MAX_SKILLS = 8;
const PITCH_MIN = 40;
const PITCH_MAX = 1200;

/**
 * Posting an idea, or editing one.
 *
 * The pitch has a floor as well as a ceiling, and the floor is the
 * interesting one: forty characters is not a quality bar, it is the
 * difference between something a stranger can decide about and a title
 * repeated twice. The ceiling is because a card in a deck has a size, and a
 * pitch that does not fit it is not read, it is scrolled past.
 */
export function IdeaForm({
  existing,
  onDone,
  onCancel,
}: {
  existing?: Idea;
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { groups, failed } = useSkills();
  const [title, setTitle] = useState(existing?.title ?? '');
  const [pitch, setPitch] = useState(existing?.pitch ?? '');
  const [stage, setStage] = useState(existing?.stage ?? '');
  const [compensationType, setCompensationType] = useState(existing?.compensationType ?? '');
  const [skills, setSkills] = useState<string[]>(
    existing?.skills.map((skill) => skill.slug) ?? [],
  );
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string>();

  const trimmedPitch = pitch.trim();
  const ready =
    title.trim().length >= 4 &&
    trimmedPitch.length >= PITCH_MIN &&
    trimmedPitch.length <= PITCH_MAX &&
    stage &&
    compensationType &&
    skills.length > 0;

  async function save(): Promise<void> {
    if (!ready || saving) return;
    setSaving(true);
    setProblem(undefined);

    // Narrowed to what the contract accepts. `ready` has already established
    // that both were chosen; the assertions are about the type of a value this
    // screen itself supplied from a fixed list.
    const body: SaveIdea = {
      title: title.trim(),
      pitch: trimmedPitch,
      stage: stage as SaveIdea['stage'],
      compensationType: compensationType as SaveIdea['compensationType'],
      skills,
    };

    try {
      if (existing) await platform.match.updateIdea(existing.id, body);
      else await platform.match.postIdea(body);
      onDone();
    } catch (error) {
      // The five-live-ideas limit and an unknown skill both arrive here, and
      // both already say what to do about it.
      setProblem(
        error instanceof PlatformApiError ? error.message : 'That did not save. Try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="column" aria-label={existing ? 'Edit your idea' : 'Post an idea'}>
      <header className="column__head">
        <h2>{existing ? 'Edit this idea' : 'Post an idea'}</h2>
        <p className="fine">
          Contributors see this without your name on it. They decide from the words, and you decide
          whether to tell them who you are.
        </p>
      </header>

      {problem ? (
        <p className="notice notice--problem" role="status" data-testid="idea-problem">
          {problem}
        </p>
      ) : null}

      <label className="field">
        <span>What is it called?</span>
        <input
          type="text"
          maxLength={120}
          value={title}
          placeholder="Sample tracking for small labs"
          data-testid="idea-title"
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>

      <label className="field">
        <span>What is it, and why you?</span>
        <textarea
          className="composer__box"
          rows={6}
          maxLength={PITCH_MAX}
          value={pitch}
          placeholder="Ten labs told me they lose track of which sample is where, and all of it is on paper today…"
          data-testid="idea-pitch"
          onChange={(event) => setPitch(event.target.value)}
        />
        <span className="fine" aria-live="polite">
          {trimmedPitch.length < PITCH_MIN
            ? `${PITCH_MIN - trimmedPitch.length} more characters before somebody can judge it`
            : `${PITCH_MAX - trimmedPitch.length} left`}
        </span>
      </label>

      <Choice label="How far along is it?" name="stage" options={STAGES} value={stage} onChange={setStage} />
      <Choice
        label="What does it offer?"
        name="compensation"
        options={COMPENSATION}
        value={compensationType}
        onChange={setCompensationType}
      />

      <div className="field">
        <span>Who are you missing?</span>
        {failed ? (
          <p className="notice notice--problem" role="status">
            We could not load the list of skills. Try again in a moment.
          </p>
        ) : (
          <SkillPicker groups={groups} chosen={skills} max={MAX_SKILLS} onChange={setSkills} />
        )}
      </div>

      <div className="choices">
        <button className="button" type="button" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
        <button
          className="button button--primary"
          type="button"
          disabled={!ready || saving}
          data-testid="save-idea"
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : existing ? 'Save changes' : 'Post it'}
        </button>
      </div>
    </section>
  );
}

function Choice({
  label,
  name,
  options,
  value,
  onChange,
}: {
  label: string;
  name: string;
  options: readonly (readonly [string, string])[];
  value: string;
  onChange: (next: string) => void;
}): React.JSX.Element {
  return (
    <fieldset className="picker__group">
      <legend className="card__label">{label}</legend>
      <div className="picker__options">
        {options.map(([option, text]) => (
          <label key={option} className={`pick${value === option ? ' pick--on' : ''}`}>
            <input type="radio" name={name} checked={value === option} onChange={() => onChange(option)} />
            {text}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
